/* eslint-disable react-hooks/exhaustive-deps */
import { useMsal } from '@azure/msal-react';
import {
  Image,
  Link,
  makeStyles,
  Menu,
  MenuTrigger,
  Select,
  Switch,
  Text,
  tokens
} from '@fluentui/react-components';
import {
  Navigation24Regular,
  Person24Regular,
  WeatherMoonRegular,
  WeatherSunnyRegular,
} from '@fluentui/react-icons';
import { useEffect, useMemo, useRef } from 'react';
import { DataService } from '../../services/dataService';
import useConfigStore from '../../store/config';

const useStyles = makeStyles({
  header: {
    padding: 0,
    backgroundColor: '#768692',
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    paddingTop: '8px',
    paddingBottom: '8px',
    height: '70px',
    width: '100%',
    '@media (max-width: 768px)': {
      height: '60px',
    },
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    height: '64px',
    padding: `0 ${tokens.spacingHorizontalL}`,
    justifyContent: 'space-between',
    backgroundColor: '#768692',
    width: '100%',
    '@media (max-width: 768px)': {
      height: '54px',
      padding: `0 ${tokens.spacingHorizontalM}`,
      gap: tokens.spacingHorizontalS,
    },
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  hamburger: {
    display: 'none',
    '@media (max-width: 768px)': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      padding: tokens.spacingHorizontalS,
      color: '#fff',
      fontSize: '24px',
      minWidth: '44px',
      minHeight: '44px',
    },
  },
  title: {
    margin: 0,
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginRight: '20px',
  },
  userMenuTrigger: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    padding: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    userSelect: 'none',
    color: '#fff',
    '& svg': {
      color: '#fff',
    },
    '&:hover, &:active, &:focus': {
      background: 'inherit',
      color: tokens.colorBrandForeground1,
      boxShadow: 'none',
      outline: 'none',
      '& svg': {
        color: tokens.colorBrandForeground1,
      },
    },
  },
  userName: {
    marginLeft: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase400,
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  themeSwitch: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    marginRight: '20px',
    '@media (max-width: 768px)': {
      marginRight: '8px',
      '& > svg': {
        display: 'none',
      },
    },
  },
  roleDropdown: {
    minWidth: '120px',
    '& > button': {
      color: '#fff',
      backgroundColor: 'transparent',
    },
    '&:hover > button': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  logo: {
    '@media (max-width: 768px)': {
      width: '50px',
      height: '50px',
    },
  },
  userButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '1.05rem',
  },
});

interface HeaderbarProps {
  onMenuClick?: () => void;
}

const Headerbar: React.FC<HeaderbarProps> = ({ onMenuClick }) => {
  const styles = useStyles();
  const { mode } = useConfigStore((state) => state.themeConfig);
  const setThemeMode = useConfigStore((state) => state.setThemeMode);
  const { selectedRole, availableRoles } = useConfigStore((state) => state.roleConfig);
  const setSelectedRole = useConfigStore((state) => state.setSelectedRole);

  const { instance, accounts } = useMsal();
  const user = accounts && accounts[0];
  const accountInfo = accounts[0] || null;
  const dataService = useMemo(() => {
    return accountInfo ? new DataService(instance, accountInfo) : null;
  }, [instance, accountInfo]);
  const ROLE_STORAGE_KEY = 'userPersonalRole';
  const PERSONAL_PARTS_STORAGE_KEY = 'userPersonalParts';

  // Track if we've already attempted to fetch roles to prevent infinite loops
  const hasAttemptedFetchRef = useRef(false);

  const fetchAndStoreRole = async (email: string) => {
    const defaultRoles = ['User'];
    try {
      if (!dataService) {
        // Fallback to default role if no dataService
        sessionStorage.setItem(ROLE_STORAGE_KEY, JSON.stringify(defaultRoles));
        return;
      }
      const response = await dataService.getUserAppPermissions(email, '2');
      let finalRoles = defaultRoles;

      if (response && response.length > 0) {
        console.log('fetchAndStoreRole called for:', email);
        const fetchedRoles = response.map((perm) => perm.rolle);
        const personalParts = response.map((perm) => perm.appPart);
        finalRoles = [...defaultRoles, ...fetchedRoles];
        sessionStorage.setItem(
          PERSONAL_PARTS_STORAGE_KEY,
          JSON.stringify(personalParts)
        );
      } else {
        console.log('No permissions returned from API for user:', email);
      }

      sessionStorage.setItem(ROLE_STORAGE_KEY, JSON.stringify(finalRoles));
      console.log(
        'Role set in sessionStorage:',
        sessionStorage.getItem(ROLE_STORAGE_KEY)
      );
      // Dispatch event for any components listening to role changes
      window.dispatchEvent(new CustomEvent('role-changed'));
      console.log('role-changed event dispatched');
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : String(e);
      console.error('Failed to fetch user role:', errorMessage);
      console.error('Full error object:', e);
      // Fallback to default User role when API call fails (e.g., CORS error)
      console.log('Setting default User role due to fetch error');
      console.log('This typically indicates: API endpoint error, CORS issue, or authentication problem');
      sessionStorage.setItem(ROLE_STORAGE_KEY, JSON.stringify(defaultRoles));
      window.dispatchEvent(new CustomEvent('role-changed'));
    }
  };

  useEffect(() => {
    console.log('Headerbar useEffect - user:', user?.username);
    // Roles are fetched in main.tsx during initialization
    // The Headerbar just reads from the config store
    if (user?.username) {
      const stored = sessionStorage.getItem(ROLE_STORAGE_KEY);
      const parsed = stored ? (JSON.parse(stored) as string[]) : null;
      console.log('Current sessionStorage role:', parsed);

      // If roles are already stored from main.tsx, don't fetch again
      if (parsed && parsed.length > 0) {
        console.log('✅ Roles already available in sessionStorage (from main.tsx initialization)');
        hasAttemptedFetchRef.current = true;
        return;
      }

      // Only fetch once if we haven't already attempted (fallback for edge cases)
      if (!hasAttemptedFetchRef.current) {
        console.log('⚠️ Roles not in sessionStorage, fetching as fallback...');
        hasAttemptedFetchRef.current = true;
        fetchAndStoreRole(user.username);
      }
    } else {
      // Don't clear sessionStorage just because user is undefined
      // This is likely a temporary state during account loading
      console.log('User undefined, keeping existing sessionStorage');
    }
  }, [user?.username]);

  const handleLogin = async () => {
    instance.loginRedirect();
    const account = instance.getAllAccounts()[0];
    if (account?.username) {
      fetchAndStoreRole(account.username);
    }
  };

  return (
    <header className={styles.header}>
      <div className={styles.container}>
        <div className={styles.leftSection}>
          <button
            className={styles.hamburger}
            onClick={onMenuClick}
            aria-label="Toggle menu"
          >
            <Navigation24Regular />
          </button>
          <Link href="/" style={{ display: 'inline-block' }}>
            <Image
              src="/imc.svg"
              alt="Logo"
              width={70}
              height={70}
              className={styles.logo}
              style={{
                margin: '10px',
                cursor: 'pointer',
              }}
            />
          </Link>
        </div>
        <div className={styles.actions}>
          {/* Role Dropdown */}
          {availableRoles.length > 0 && (
            <Select
              className={styles.roleDropdown}
              value={selectedRole || ''}
              onChange={(_, data) => {
                const selectedValue = data.value;
                if (selectedValue) {
                  setSelectedRole(selectedValue);
                  console.log('Selected role:', selectedValue);
                  window.dispatchEvent(new CustomEvent('role-changed', { detail: { role: selectedValue } }));
                }
              }}
            >
              {availableRoles.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </Select>
          )}
          <div className={styles.themeSwitch}>
            <WeatherSunnyRegular />
            <Switch
              title="Toggle theme"
              aria-checked={mode === 'dark'}
              checked={mode === 'dark'}
              onChange={(_, data) =>
                setThemeMode(data.checked ? 'dark' : 'light')
              }
            />
            <WeatherMoonRegular />
          </div>

          {/* Auth User Info / Login */}
          {user ? (
            <Menu>
              <MenuTrigger>
                <div className={styles.userMenuTrigger} tabIndex={0}>
                  <Person24Regular style={{ fontSize: 28 }} />
                  <Text className={styles.userName}>
                    {user.name || user.username}
                  </Text>
                </div>
              </MenuTrigger>
            </Menu>
          ) : (
            <div
              className={styles.userMenuTrigger}
              tabIndex={0}
              onClick={handleLogin}
              style={{ fontWeight: 500 }}
            >
              <Person24Regular style={{ fontSize: 28 }} />
              <Text className={styles.userName}>Anmelden</Text>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Headerbar;
